/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The signing algorithm of the key.
 */
export const AwsKmsKeyAlgorithm = {
  Rs256: "RS256",
  Es256: "ES256",
} as const;
/**
 * The signing algorithm of the key.
 */
export type AwsKmsKeyAlgorithm = OpenEnum<typeof AwsKmsKeyAlgorithm>;

/**
 * The cloud KMS provider of the key.
 */
export const AwsKmsKeyProvider = {
  AwsKms: "aws_kms",
  GcpKms: "gcp_kms",
} as const;
/**
 * The cloud KMS provider of the key.
 */
export type AwsKmsKeyProvider = OpenEnum<typeof AwsKmsKeyProvider>;

/**
 * An AWS KMS external key.
 */
export type AwsKmsKey = {
  /**
   * The signing algorithm of the key.
   */
  algorithm: AwsKmsKeyAlgorithm;
  /**
   * When the key was created.
   */
  createdAt: Date;
  /**
   * The Speakeasy identity (GCP service-account email or AWS principal ARN) the customer granted on the key for the key-policy / IAM-grant model. Not a secret.
   */
  customerGrantReference?: string | undefined;
  /**
   * The external credential Speakeasy uses to authenticate to the key.
   */
  externalCredentialId: string;
  /**
   * The ID of the external key.
   */
  id: string;
  /**
   * The ARN of the AWS KMS key.
   */
  keyArn: string;
  /**
   * A human-readable name for the key.
   */
  name: string;
  /**
   * The organization that owns the key.
   */
  organizationId: string;
  /**
   * The cloud KMS provider of the key.
   */
  provider: AwsKmsKeyProvider;
  /**
   * When the key was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const AwsKmsKeyAlgorithm$inboundSchema: z.ZodMiniType<
  AwsKmsKeyAlgorithm,
  unknown
> = openEnums.inboundSchema(AwsKmsKeyAlgorithm);

/** @internal */
export const AwsKmsKeyProvider$inboundSchema: z.ZodMiniType<
  AwsKmsKeyProvider,
  unknown
> = openEnums.inboundSchema(AwsKmsKeyProvider);

/** @internal */
export const AwsKmsKey$inboundSchema: z.ZodMiniType<AwsKmsKey, unknown> = z
  .pipe(
    z.object({
      algorithm: AwsKmsKeyAlgorithm$inboundSchema,
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      customer_grant_reference: z.optional(z.string()),
      external_credential_id: z.string(),
      id: z.string(),
      key_arn: z.string(),
      name: z.string(),
      organization_id: z.string(),
      provider: AwsKmsKeyProvider$inboundSchema,
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "customer_grant_reference": "customerGrantReference",
        "external_credential_id": "externalCredentialId",
        "key_arn": "keyArn",
        "organization_id": "organizationId",
        "updated_at": "updatedAt",
      });
    }),
  );

export function awsKmsKeyFromJSON(
  jsonString: string,
): SafeParseResult<AwsKmsKey, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AwsKmsKey$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AwsKmsKey' from JSON`,
  );
}
